import React, { useMemo, useRef, useState } from "react";
import "./changeLog.scss";
import Table from "ui-components/table/table";
import { getChangeLogColumns } from "./changeLogColumn";
import { Form, Formik, FormikProps } from "formik";
import LabeledDateRangeInput from "ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import Button, { ButtonThemes } from "ui-components/button/button";
import { useInfiniteQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import AddressmanagerService from "app/data/address-manager/addressService";
import { useSelector } from "react-redux";
import { addressSelector } from "slices/address-manager/addressSlice";
import moment from "moment";

interface changeLogFilterModel {
  dateFrom?: string;
  dateTo?: string;
}

const ChangelogWidget: React.FC = () => {
  const { selectedAddressId } = useSelector(addressSelector);
  const filterRef = useRef<FormikProps<changeLogFilterModel>>(null);
  const [filters, setFilters] = useState<changeLogFilterModel>({
    dateFrom: "",
    dateTo: "",
  });
  const {
    data: addressChangeLogData,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isPreviousData
  } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.ADDRESS_CHANGE_LOG, selectedAddressId, filters],
    queryFn: async ({ pageParam = 0 }) => {
      return await AddressmanagerService.getInstance().getChangeLog(
        selectedAddressId as string,
        pageParam,
        filters.dateFrom ?? "",
        filters.dateTo ?? ""
      );
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.last) return undefined;
      return lastPage.pageable.pageNumber + 1;
    },
    keepPreviousData: false,
    retry: false,
  });

  const items = useMemo(() => addressChangeLogData?.pages.flatMap((page) => page.content) ?? [], [addressChangeLogData]);

  const diffsAfterSplitting = useMemo(() => {
    let diffs: {
      modifiedAt: string;
      modifiedBy: string;
      changeDescription: string;
      fullChangeDescription: string;
      showFullChangeDescription: boolean;
    }[] = [];

    if (!items) return [];


    items.forEach((item) => {
      const lines = item.changeDescription
        .split(/\|/)
        .filter(line => line.trim() !== "");

      lines.forEach((line, index) => {
        const maxLength = 100;
        const trimmedLine = line.trim();
        const isLongLine = trimmedLine.length > maxLength;

        const changeDescription =
          isLongLine
            ? trimmedLine.slice(0, maxLength) + "..."
            : trimmedLine;

        diffs.push({
          modifiedAt: item.modifiedAt,
          modifiedBy: item.modifiedBy,
          changeDescription: changeDescription,
          showFullChangeDescription: isLongLine,
          fullChangeDescription: trimmedLine,
        });
      });
    });

    return diffs;
  }, [items]);


  return <>
    <div className="address-manager-changelog">
      <div className="sub-title">Change Log</div>
      <Formik
        initialValues={{
          dateFrom: undefined,
          dateTo: undefined,
        }}
        onSubmit={(values) => {
          setFilters({
            dateFrom: values.dateFrom ? moment(values.dateFrom, "MM/DD/YYYY").format("YYYY-MM-DD") : "",
            dateTo: values.dateTo ? moment(values.dateTo, "MM/DD/YYYY").format("YYYY-MM-DD") : "",
          })
        }}
        innerRef={filterRef}
      >
        {(props: FormikProps<changeLogFilterModel>) => (
          <Form>
            <div className="date-filter-range-input">
              <LabeledDateRangeInput
                label="Filter by Date Range:"
                labelMode={LabelModes.column}
                start={props.values.dateFrom}
                end={props.values.dateTo}
                maxDate={new Date()}
                onStartChange={(v) => {
                  props.setFieldValue("dateFrom", v || undefined);
                  props.submitForm();
                }}
                onEndChange={(v) => {
                  props.setFieldValue("dateTo", v || undefined);
                  props.submitForm();
                }}
              />
              <Button
                type="submit"
                theme={ButtonThemes.blue}
              >
                Apply Filter
              </Button>
            </div>
          </Form>
        )}
      </Formik>
      <div className="content">
        <Table
          infiniteScroll={true}
          data={diffsAfterSplitting}
          rowHeight={58}
          responsive={true}
          keepTableOnLoading
          infiniteScrollLoading={isFetchingNextPage}
          columns={getChangeLogColumns()}
          isLoading={isLoading || (isFetching && isPreviousData)}
          cursorPointer
          minTableHeight={420}
          onInfiniteScroll={() => fetchNextPage()}
          infiniteScrollHasNext={!addressChangeLogData?.pages.some(
            (page) => page.content.length !== 50
          )}
        />
      </div>
    </div>
  </>;
};

export default ChangelogWidget;
